import React, { useState, useEffect, useRef, memo, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useGetConfiguration,
  useGetActiveInteractionIDs,
  useGetActiveAmbientLighting,
  useInteractionStore,
  Interaction,
  InteractionGroup,
} from '@adloid-technologies/discover-sdk';
import { handleAmbientLightingChange } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/ambient-lighting';
import { assetUrl, uimetadata, metadata } from '../../utils/metadata';
import { useTranslation, useRTL } from '../../utils/translations';
import { useLogEvent, AnalyticsIds } from '../../utils/useAnalytics';

/* ── Theme context so sub-components can read isDarkMode without prop-drilling ── */
const DarkModeCtx = createContext(true);

/* ── Icon map: keyed by label slug ─────────────────────────────────── */
const ICON_MAP: Record<string, string> = {
  doors:      `${assetUrl}icons/interactions/doors.svg`,
  door:       `${assetUrl}icons/interactions/doors.svg`,
  // Hummer EV's E-Trunk is the front trunk, so it gets the bonnet icon - and it
  // has to be matched before "trunk" or it would fall through to the boot icon
  // and the panel would show two identical boot thumbnails.
  'e-trunk':  `${assetUrl}icons/interactions/e-trunk.png`,
  'e trunk':  `${assetUrl}icons/interactions/e-trunk.png`,
  frunk:      `${assetUrl}icons/interactions/e-trunk.png`,
  bonnet:     `${assetUrl}icons/interactions/e-trunk.png`,
  hood:       `${assetUrl}icons/interactions/e-trunk.png`,
  boot:       `${assetUrl}icons/interactions/boot.svg`,
  trunk:      `${assetUrl}icons/interactions/boot.svg`,
  sunroof:    `${assetUrl}icons/interactions/sunroof.svg`,
  roof:       `${assetUrl}icons/interactions/sunroof.svg`,
  headlight:  `${assetUrl}icons/interactions/headlight.svg`,
  drl:        `${assetUrl}icons/interactions/drl.svg`,
  taillight:  `${assetUrl}icons/interactions/taillight.svg`,
  // Matched on the two-word phrase so "defogger"/"defroster" can't collide.
  'fog lamp':  `${assetUrl}icons/interactions/foglamp.svg`,
  'fog light': `${assetUrl}icons/interactions/foglamp.svg`,
  suspension: `${assetUrl}icons/interactions/suspension.svg`,
  seat:       `${assetUrl}icons/interactions/seat.svg`,
  // "2nd Row Folding" omits the word "seat" — match the action too.
  folding:    `${assetUrl}icons/interactions/seat.svg`,
  indicator:  `${assetUrl}icons/interactions/indicator.svg`,
};

function resolveIcon(labelEn: string): string {
  const lc = labelEn.toLowerCase();
  const match = Object.keys(ICON_MAP).find((k) => lc.includes(k));
  return match ? ICON_MAP[match] : `${assetUrl}icons/interactions/carLogo.svg`;
}

/* ── Circle interaction button — selected/loading injected by SDK ── */
const InteractionBtn = memo(
  ({
    icon,
    selected = false,
    loading = false,
  }: {
    icon: string;
    selected?: boolean;
    loading?: boolean;
  }) => {
    const isDark = useContext(DarkModeCtx);
    return (
      <div
        className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer',
          selected
            ? isDark ? 'bg-white' : 'bg-black'
            : isDark ? 'bg-white/5 hover:bg-white/15' : 'bg-black/5 hover:bg-black/10',
        )}
      >
        {/* Always show the icon itself, coloured for selected/deselected - never
            swap in a loading spinner. Every interaction technically has a brief
            `loading:true` window between click and the SDK's callback, but it
            normally resolves within a microtask so nobody ever sees it; a
            flicker-type interaction (Suspension) runs its cycle on real
            setTimeout ticks instead, long enough for the spinner to actually
            render - showing a ring in place of the icon instead of just
            recolouring it like every other interaction does. */}
        <img
          src={icon}
          alt=""
          className={cn(
            'w-5 h-5 object-contain',
            selected
              ? isDark ? 'brightness-0' : 'brightness-0 invert'
              : isDark ? 'brightness-0 invert' : 'brightness-0',
          )}
          onError={(e) => {
            (e.target as HTMLImageElement).src = `${assetUrl}icons/interactions/carLogo.svg`;
          }}
          draggable={false}
        />
      </div>
    );
  },
);
InteractionBtn.displayName = 'InteractionBtn';

/* ── Mobile compact circle button (30.7px, Figma 1201:2258 × 1.5) ── */
const MobileInteractionBtn = memo(
  ({
    icon,
    selected = false,
    loading = false,
  }: {
    icon: string;
    selected?: boolean;
    loading?: boolean;
  }) => (
    <div
      style={{ width: '30.7px', height: '30.7px' }}
      className={cn(
        'rounded-full flex items-center justify-center border-[0.5px] border-solid transition-all cursor-pointer flex-shrink-0',
        selected ? 'bg-[var(--brand-accent)] border-[#e6e6e6]' : 'bg-white border-[#e6e6e6]',
      )}
    >
      {/* Always the icon, never a loading spinner - see InteractionBtn above. */}
      <img
        src={icon}
        alt=""
        style={{ width: '14px', height: '14px' }}
        className={cn('object-contain', selected ? 'brightness-0 invert' : 'brightness-0')}
        onError={(e) => { (e.target as HTMLImageElement).src = `${assetUrl}icons/interactions/carLogo.svg`; }}
        draggable={false}
      />
    </div>
  ),
);
MobileInteractionBtn.displayName = 'MobileInteractionBtn';

/* ── Ambient lighting trigger button ─────────────────────────────── */
const AmbientBtn: React.FC<{ onClick: () => void; compact?: boolean }> = ({ onClick, compact = false }) => {
  const isDark = useContext(DarkModeCtx);
  if (compact) {
    // Sized to match MobileInteractionBtn (30.7px) so it sits flush in the
    // mobile compact pill alongside the other interaction icons.
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        style={{ width: '30.7px', height: '30.7px' }}
        className="rounded-full flex items-center justify-center border-[0.5px] border-solid border-[#e6e6e6] bg-white transition-all relative flex-shrink-0"
      >
        <img
          src={`${assetUrl}icons/interactions/indicator.svg`}
          alt="Ambient Light"
          style={{ width: '14px', height: '14px' }}
          className="object-contain brightness-0"
          draggable={false}
        />
        <div
          className="absolute bottom-0 right-0 w-2 h-2 rounded-full ring-2 ring-white/90"
          style={{ background: 'linear-gradient(135deg,#4556A7,#24B29C,#F6962A,#B5302A)' }}
        />
      </button>
    );
  }
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        'w-10 h-10 rounded-full flex items-center justify-center transition-all relative',
        isDark ? 'bg-white/5 hover:bg-white/15' : 'bg-black/5 hover:bg-black/10',
      )}
    >
      <img
        src={`${assetUrl}icons/interactions/indicator.svg`}
        alt="Ambient Light"
        className={cn('w-5 h-5 object-contain', isDark ? 'brightness-0 invert' : 'brightness-0')}
        draggable={false}
      />
      {/* coloured dot indicator */}
      <div
        className={cn('absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full',
          isDark ? 'ring-2 ring-black/90' : 'ring-2 ring-white/90')}
        style={{ background: 'linear-gradient(135deg,#4556A7,#24B29C,#F6962A,#B5302A)' }}
      />
    </button>
  );
};

/* ── Ambient colour slider — always HORIZONTAL ─────────────────────
 * The SDK's <AmbientLighting/> picks orientation from react-device-detect
 * (isVertical = isMobile), so on real phones it renders a 30x200 vertical
 * bar that overflows the pill. This is a horizontal clone used on BOTH
 * mobile and desktop for a consistent UI; it reuses the SDK's own
 * handleAmbientLightingChange for the actual colour application
 * (identifier/sku resolution stays in the SDK).
 */
const HorizontalAmbientSlider: React.FC = () => {
  const barRef = useRef<HTMLDivElement>(null);
  const modelId = uimetadata.config.model;
  const ambient = (metadata as any)?.models?.[modelId]?.ambientLighting;
  const ambientColors: string = ambient?.ambientColors ?? '';
  const stops: string[] = Object.keys(ambient?.ambientLightings ?? {});
  const sections = stops.length;

  const defaultIndex = (() => {
    const entries = Object.values(ambient?.ambientLightings ?? {}) as any[];
    const i = entries.findIndex((v) => v?.isDefault);
    return i >= 0 ? i : 0;
  })();

  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);

  if (!ambientColors || sections <= 1) return null;

  const handlePick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const clickPos = e.clientX - rect.left;
    const sectionSize = rect.width / (sections - 1);
    const idx = Math.min(sections - 1, Math.max(0, Math.round(clickPos / sectionSize)));
    setSelectedIndex(idx);
    handleAmbientLightingChange(idx);
  };

  // Indicator sits as a fraction across the bar so it tracks width exactly.
  const leftPct = sections > 1 ? (selectedIndex / (sections - 1)) * 100 : 0;

  return (
    <div
      ref={barRef}
      onClick={handlePick}
      className="relative w-full cursor-pointer"
      style={{
        height: '24px',
        borderRadius: '50px',
        backgroundImage: `linear-gradient(to right, ${ambientColors})`,
      }}
    >
      <div
        className="absolute top-1/2 w-[18px] h-[18px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.35)] border border-black/10 pointer-events-none"
        style={{ left: `${leftPct}%`, transform: 'translate(-50%, -50%)' }}
      />
    </div>
  );
};

/* ── Main panel ──────────────────────────────────────────────────── */
interface InteractionsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  panelClassName?: string;
  vertical?: boolean;
  mobile?: boolean;
}

const VISIBLE_COUNT = 3;

export const InteractionsPanel: React.FC<InteractionsPanelProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  panelClassName,
  vertical = false,
  mobile = false,
}) => {
  const { variantID, view } = useGetConfiguration();
  const viewType = view?.type === 'interior' ? 'interior' : 'exterior';
  const isInterior = viewType === 'interior';

  const { t } = useTranslation();
  const isRTL = useRTL();
  const modelId = uimetadata.config.model;
  const modelInteractions = (uimetadata.models as any)[modelId]?.interactions ?? {};

  const { interactionIDs } = useGetActiveInteractionIDs(variantID, viewType);
  const { ambientAvailable } = useGetActiveAmbientLighting();
  const { interactions } = useInteractionStore();
  const { trackEvent } = useLogEvent();

  const [startIndex, setStartIndex] = useState(0);
  const [showAmbientSlider, setShowAmbientSlider] = useState(false);

  /* reset when view changes or panel opens/closes */
  useEffect(() => {
    setStartIndex(0);
    setShowAmbientSlider(false);
  }, [viewType, isOpen]);

  useEffect(() => {
    if (isOpen) {
      trackEvent(AnalyticsIds.interactionInteract, {
        state: true,
        parent: viewType,
        primaryTypeName: 'panel',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    trackEvent(AnalyticsIds.interactionInteract, {
      state: false,
      parent: viewType,
      primaryTypeName: 'panel',
    });
    onClose();
  };

  const pageIndex = Math.floor(startIndex / VISIBLE_COUNT) + 1;

  const handleInteractionClick = (_type: 'interaction', id: string) => {
    // Interaction's own onClick fires the SDK's addInteractionID/
    // removeInteractionID synchronously first, but that doesn't re-render
    // this component before our handler runs — `interactions` here is still
    // the pre-click snapshot, so its inverse is the NEW state per the sheet's
    // "always pass the NEW state" rule.
    const wasOn = interactions[id]?.state ?? false;
    trackEvent(AnalyticsIds.interactionInteract, {
      state: !wasOn,
      parent: viewType,
      primaryTypeName: modelInteractions[id]?.label?.en ?? id,
      secondaryTypeName: String(pageIndex),
    });
  };

  const handlePrev = () => {
    setStartIndex((p) => Math.max(0, p - 1));
    trackEvent(AnalyticsIds.interactionInteract, {
      parent: 'interactionsPanel',
      primaryTypeName: 'prev',
      secondaryTypeName: String(Math.max(1, pageIndex - 1)),
    });
  };
  const handleNext = () => {
    setStartIndex((p) => p + 1);
    trackEvent(AnalyticsIds.interactionInteract, {
      parent: 'interactionsPanel',
      primaryTypeName: 'next',
      secondaryTypeName: String(pageIndex + 1),
    });
  };

  const visibleIDs = interactionIDs.slice(startIndex, startIndex + VISIBLE_COUNT);
  const isAtEnd = startIndex + VISIBLE_COUNT >= interactionIDs.length;
  const showAmbientBtn = isInterior && ambientAvailable;

  return (
    <DarkModeCtx.Provider value={isDarkMode}>
    <AnimatePresence>
      {isOpen && (
        <div
          className={vertical ? "contents" : (panelClassName ?? (isRTL ? "absolute bottom-full right-1/2 translate-x-1/2 flex flex-col items-center gap-4 z-50 w-max" : "absolute bottom-full left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 z-50 w-max"))}
          onClick={(e) => e.stopPropagation()}
          style={{ marginBottom: !mobile? '15px':'' }}
        >
          {mobile ? (
            /* ── MOBILE compact pill (Figma 1201:2258) ───────────────── */
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 28 }}
              className="flex flex-col items-center"
              style={{ direction: 'ltr' }}
            >
              {/* Close (icon pill) / back (ambient slider) — 23.6px circle above pill (Figma 15.728px × 1.5) */}
              <button
                onClick={() => (showAmbientSlider ? setShowAmbientSlider(false) : handleClose())}
                className="rounded-full bg-white border border-[#e6e6e6] flex items-center justify-center shadow-sm mb-[8px]"
                style={{ width: '23.6px', height: '23.6px' }}
              >
                {showAmbientSlider
                  ? <ArrowLeft size={10} strokeWidth={2.5} className="text-[#333]" />
                  : <X size={10} strokeWidth={2.5} className="text-[#333]" />}
              </button>

              {!showAmbientSlider ? (
                <>
                  {/* Horizontal pill (Figma 115.124×28.781px × 1.5) */}
                  <div
                    className="bg-white flex items-center shadow-[0_2px_12px_rgba(0,0,0,0.2)]"
                    style={{
                      width: showAmbientBtn ? '207px' : '172.7px',
                      height: '43.2px',
                      borderRadius: 'var(--brand-pill-radius)',
                      border: '0.5px solid #e6e6e6',
                    }}
                  >
                    {/* Prev arrow */}
                    <button
                      onClick={handlePrev}
                      disabled={startIndex === 0}
                      className={cn(
                        'flex items-center justify-center h-full flex-shrink-0 transition-opacity',
                        startIndex === 0 ? 'opacity-15' : 'opacity-60 hover:opacity-100',
                      )}
                      style={{ width: '33px' }}
                    >
                      <ChevronLeft size={14} className="text-black" />
                    </button>

                    {/* Icon buttons */}
                    <InteractionGroup className="flex items-center gap-[6px] flex-1 justify-center">
                      <AnimatePresence mode="popLayout" initial={false}>
                        {visibleIDs.map((id) => {
                          const labelEn = modelInteractions[id]?.label?.en ?? id;
                          const icon = resolveIcon(labelEn);
                          return (
                            <motion.div
                              key={id}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                            >
                              <Interaction interactionId={id} onClick={handleInteractionClick}>
                                <MobileInteractionBtn icon={icon} />
                              </Interaction>
                            </motion.div>
                          );
                        })}
                        {showAmbientBtn && (
                          <motion.div
                            key="ambient-btn"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                          >
                            <AmbientBtn compact onClick={() => setShowAmbientSlider(true)} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </InteractionGroup>

                    {/* Next arrow */}
                    <button
                      onClick={handleNext}
                      disabled={isAtEnd}
                      className={cn(
                        'flex items-center justify-center h-full flex-shrink-0 transition-opacity',
                        isAtEnd ? 'opacity-15' : 'opacity-60 hover:opacity-100',
                      )}
                      style={{ width: '35.3px' }}
                    >
                      <ChevronRight size={14} className="text-black" />
                    </button>
                  </div>

                  {/* Downward caret — points toward toolbar */}
                  <svg width="18" height="11" viewBox="0 0 18 11" fill="none" className="mt-[-0.5px] cursor-pointer" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.10))' }}>
                    <path d="M0 0L9 11L18 0Z" fill="white" />
                  </svg>
                </>
              ) : (
                <>
                  {/* Ambient light slider — mobile-width white pill (horizontal) */}
                  <div
                    className="bg-white px-2.5 flex items-center shadow-[0_2px_12px_rgba(0,0,0,0.2)] border-[0.5px] border-[#e6e6e6]"
                    style={{ width: '207px', height: '52px', borderRadius: 'var(--brand-pill-radius)' }}
                  >
                    <HorizontalAmbientSlider />
                  </div>

                  {/* Downward caret — points toward toolbar */}
                  <svg width="18" height="11" viewBox="0 0 18 11" fill="none" className="mt-[-0.5px] cursor-pointer" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.10))' }}>
                    <path d="M0 0L9 11L18 0Z" fill="white" />
                  </svg>
                </>
              )}
            </motion.div>
          ) : vertical ? (
            /* ── VERTICAL MODE (mobile) — close button above + dark pill ── */
            <div className="flex flex-col items-center gap-2">
              {/* Close button — outside/above the pill */}
              <button
                onClick={handleClose}
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                  isDarkMode ? 'bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]' : 'bg-white text-black hover:bg-white/80 shadow-sm'
                )}
              >
                <X size={14} strokeWidth={2.5} />
              </button>

            <motion.div
              initial={{ opacity: 0, x: -12, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -12, scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              className={cn(
                'rounded-[var(--brand-pill-radius)] px-[6px] py-[8px] flex flex-col items-center gap-[8px] shadow-2xl',
                isDarkMode ? 'bg-[#141414]' : 'bg-white border border-black/8'
              )}
            >
              {/* Scroll up */}
              {startIndex > 0 && (
                <button
                  onClick={handlePrev}
                  className={cn('transition-colors', isDarkMode ? 'text-white/40 hover:text-white' : 'text-black/30 hover:text-black')}
                >
                  <ChevronUp size={16} />
                </button>
              )}

              <InteractionGroup className="flex flex-col items-center gap-[8px]">
                {visibleIDs.map((id) => {
                  const labelEn = modelInteractions[id]?.label?.en ?? id;
                  const icon = resolveIcon(labelEn);
                  return (
                    <motion.div
                      key={id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                    >
                      <Interaction interactionId={id} onClick={handleInteractionClick}>
                        <InteractionBtn icon={icon} />
                      </Interaction>
                    </motion.div>
                  );
                })}
              </InteractionGroup>

              {/* Ambient btn */}
              {showAmbientBtn && !showAmbientSlider && (
                <AmbientBtn onClick={() => setShowAmbientSlider(true)} />
              )}

              {/* Scroll down */}
              {!isAtEnd && (
                <button
                  onClick={handleNext}
                  className={cn('transition-colors', isDarkMode ? 'text-white/40 hover:text-white' : 'text-black/30 hover:text-black')}
                >
                  <ChevronDown size={16} />
                </button>
              )}
            </motion.div>
            </div>
          ) : (
          !showAmbientSlider ? (
            <>
              {/* Close button */}
              <button
                onClick={handleClose}
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                  isDarkMode
                    ? 'bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]'
                    : 'bg-white text-black hover:bg-white/80 shadow-sm',
                )}
              >
                <X size={16} />
              </button>

              {/* Carousel pill */}
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.9 }}
                className={cn(
                  'backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-4 shadow-2xl',
                  isDarkMode
                    ? 'bg-black/95 border border-white/10'
                    : 'bg-white/95 border border-black/8 shadow-xl',
                )}
              >
                {/* Prev chevron */}
                <button
                  onClick={handlePrev}
                  disabled={startIndex === 0}
                  className={cn(
                    'transition-colors',
                    startIndex === 0
                      ? isDarkMode ? 'text-white/10' : 'text-black/15'
                      : isDarkMode ? 'text-white/40 hover:text-white' : 'text-black/40 hover:text-black',
                  )}
                >
                  <ChevronLeft size={20} />
                </button>

                {/* Interaction cards + optional ambient btn */}
                <div className="flex items-center gap-2">
                  <AnimatePresence mode="popLayout" initial={false}>
                    <InteractionGroup className="flex items-center gap-2">
                      {visibleIDs.map((id) => {
                        const labelEn = modelInteractions[id]?.label?.en ?? id;
                        const icon = resolveIcon(labelEn);
                        return (
                          <motion.div
                            key={id}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                          >
                            <Interaction interactionId={id} onClick={handleInteractionClick}>
                              <InteractionBtn icon={icon} />
                            </Interaction>
                          </motion.div>
                        );
                      })}
                    </InteractionGroup>

                    {/* Ambient button — only INT view, only when at start (always visible) */}
                    {showAmbientBtn && (
                      <motion.div
                        key="ambient-btn"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                      >
                        <AmbientBtn onClick={() => setShowAmbientSlider(true)} />
                      </motion.div>
                    )}


                  </AnimatePresence>
                </div>

                {/* Next chevron */}
                <button
                  onClick={handleNext}
                  disabled={isAtEnd}
                  className={cn(
                    'transition-colors',
                    isAtEnd
                      ? isInterior ? 'text-[var(--brand-accent)] opacity-20' : isDarkMode ? 'text-white/10' : 'text-black/15'
                      : isInterior ? 'text-[var(--brand-accent)] hover:scale-110' : isDarkMode ? 'text-white/40 hover:text-white' : 'text-black/40 hover:text-black',
                  )}
                >
                  <ChevronRight size={20} />
                </button>
              </motion.div>
            </>
          ) : (
            <>
              {/* Back to carousel — same solid bg treatment as the close (X)
                  button above, not the previous bg-black/5 tint, which was
                  invisible against a light page background. */}
              <button
                onClick={() => setShowAmbientSlider(false)}
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center transition-colors mb-3',
                  isDarkMode
                    ? 'bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]'
                    : 'bg-white text-black hover:bg-white/80 shadow-sm',
                )}
              >
                <ArrowLeft size={18} />
              </button>

              {/* Ambient light slider — horizontal bar inside white pill (same UI as mobile) */}
              <motion.div
                initial={{ opacity: 0, y: 15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 15, scale: 0.95 }}
                className="relative"
              >
                <div
                  className="bg-white rounded-[var(--brand-pill-radius)] px-2.5 flex items-center w-[280px] shadow-2xl relative z-10 border-[3px] border-white overflow-hidden"
                  style={{ height: '52px' }}
                >
                  <HorizontalAmbientSlider />
                </div>

                {/* Downward pointer caret */}
                <div className="absolute left-1/2 -bottom-3 -translate-x-1/2 flex justify-center">
                  <svg width="24" height="12" viewBox="0 0 24 12" fill="none">
                    <path d="M12 12C9.5 12 7.5 8 6 6L0 0H24L18 6C16.5 8 14.5 12 12 12Z" fill="white" />
                  </svg>
                </div>
              </motion.div>
            </>
          ) )}
        </div>
      )}
    </AnimatePresence>
    </DarkModeCtx.Provider>
  );
};
